import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { AuthenticatedTemplate } from '@azure/msal-react';
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { identityService } from '../../services/identityService';
import { UserDetailGrid } from '../../components/UserDetailGrid/UserDetailGrid';
import { subscribedAccessService } from '../../services/subscribedAccessService';
import { ISubscribedAccess, SUBSCRIBED_ACCESS_TAB_VALUES } from '../../types/subscribed-access';
import TabPanel from '../../components/General/TabPanel';
import { UserDetailPopup } from '../../components/UserDetailsPopup/UserDetailPopup';
import { Spinner } from '../../components/General/Spinner';
import UserActivityDataGrid from '../../components/UserActivityDataGrid';
import { CUSSUP_ROLES } from '../../types/auth';
import UserConsentDataGrid from '../../components/UserConsentDataGrid';
import { useAlert } from '../../providers/AlertProvider';
import RoleAuthorizeShowGuard from '../../Guards/RoleAuthorizeShowGuard';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import { IActivity } from '../../types/activity';
import usePagedCollection, { UsePagedCollectionHookResult } from '../../hooks/usePagedCollection';
import useCollection, { UseCollectionHookResult } from '../../hooks/useCollection';
import { useNavigate, useParams } from 'react-router';
import UseIdentity from '../../hooks/useIdentity';
import { USER_DETAIL_POPUP_TYPE } from '../../types/user-detail-popup-type';
import SubscribedAccessHistoryTableContainer from '../../components/SubscribedAccess/SubscribedAccessTable/SubscribedAccessHistoryTableContainer';
import { useAuth } from '../../providers/AuthProvider';
import { entitlementsService } from '../../services/entitlementService';
import EntitlementsEnhancedDataGrid from '../../components/Entitlements/EntitlementsEnhancedDataGrid';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import EntitlementsTableContainer from '../../components/Entitlements/ClaimsTable/EntitlementsTableContainer';
import { EntitlementRowData } from '../../components/Entitlements/ClaimsTable/entitlements-ui-types';
import { Entitlement } from '../../types/entitlements';
import {
  DIALOG_TYPE,
  EntitlementDialog,
} from '../../components/Entitlements/EntitlementsDialog/EntitlementsDialog';
import SubscriptionsTableContainer from '../../components/Subscriptions/SubscriptionsTableContainer';
import { ISubscription, IIdentityRoleAssignment } from '../../types/subscription';
import { subscriptionRoleService } from '../../services/subscriptionRoleService';
import { useCursorPagination } from '../../hooks/useCursorPagination';

export const UserDetail: FC = () => {
  const { identityId } = useParams();
  const identityIdParam = useMemo(() => decodeURI(identityId as string), [identityId]);
  const identityHook = UseIdentity(identityIdParam);
  const { identity, identityLoading, identityError, getIdentity, invalidateAccessTokens } =
    identityHook;
  const { roles } = useAuth();

  const [allUserDataLoading, setAllUserDataLoading] = useState(false);
  const [invalidateSessionsLoading, setInvalidateSessionsLoading] = useState(false);
  const [userDetailPopupType, setUserDetailPopupType] = useState<
    USER_DETAIL_POPUP_TYPE | undefined
  >(undefined);
  const [showEditUserDetailPopup, setShowUserDetailPopup] = useState<boolean>(false);
  const [entitlementDialogType, setEntitlementDialogType] = useState<DIALOG_TYPE>(DIALOG_TYPE.ADD);
  const [entitlementData, setEntitlementData] = useState<Entitlement>();
  const [showEntitlementDialog, setShowEntitlementDialog] = useState<boolean>(false);
  const [entitlementsView, setEntitlementsView] = useState<'classic' | 'enhanced'>('enhanced');

  const [tabValue, setTabValue] = useState<string>(SUBSCRIBED_ACCESS_TAB_VALUES.CURRENT);
  const [editModeEnabled, setEditMode] = useState(false);
  const [isSavingEditedUserData, setIsSavingEditedUserData] = useState(false);
  const alert = useAlert();
  const isSocialAccount = useMemo(
    () => identity?.id.startsWith('oauth2|NRCM|') || identity?.id.startsWith('google-oauth2|'),
    [identity]
  );

  const { t } = useTranslation();
  const navigate = useNavigate();

  const entitlementsCollectionHook: UseCollectionHookResult<Entitlement> = useCollection();
  const subscribedAccessesHistoryCollectionHook: UsePagedCollectionHookResult<ISubscribedAccess> =
    usePagedCollection();
  const activityLogsCollectionHook: UsePagedCollectionHookResult<IActivity> = usePagedCollection();

  // Cursor-based pagination state for subscriptions
  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
  const [roleAssignments, setRoleAssignments] = useState<IIdentityRoleAssignment[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptionsError, setSubscriptionsError] = useState(false);
  const [subscriptionsTotalCount, setSubscriptionsTotalCount] = useState(0);
  const subscriptionsPagination = useCursorPagination(10);

  useEffect(() => {
    getIdentity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSubscriptions = async () => {
    if (!identity) return;
    setSubscriptionsLoading(true);
    setSubscriptionsError(false);
    try {
      const roleAssignmentsData = await subscriptionRoleService.getRoleAssignmentsByIdentityId(
        identity.id,
        subscriptionsPagination.cursor,
        subscriptionsPagination.pageSize,
        'Forward'
      );

      // Store raw role assignments for pagination
      setRoleAssignments([...roleAssignmentsData.items]);

      // Convert role assignments to subscriptions format
      const subscriptionsMap = new Map<string, ISubscription>();
      roleAssignmentsData.items.forEach((roleAssignment: IIdentityRoleAssignment) => {
        const key = `${roleAssignment.subscriptionReference.system}_${roleAssignment.subscriptionReference.id}`;
        if (!subscriptionsMap.has(key)) {
          subscriptionsMap.set(key, {
            system: roleAssignment.subscriptionReference.system,
            id: roleAssignment.subscriptionReference.id,
            roleAssignments: [],
          });
        }
        subscriptionsMap.get(key)!.roleAssignments.push({
          identityId: roleAssignment.identityId,
          roleName: roleAssignment.roleName,
        });
      });

      setSubscriptions(Array.from(subscriptionsMap.values()));
      setSubscriptionsTotalCount(roleAssignmentsData.totalCount);
    } catch (err) {
      setSubscriptionsError(true);
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  useEffect(() => {
    if (!identity) return;
    entitlementsCollectionHook.setCollectionLoader(
      () => () => entitlementsService.getEntitlements(identity.id)
    );
    subscribedAccessesHistoryCollectionHook.setCollectionLoader(
      () => (pageSize: number, pageNumber: number) =>
        subscribedAccessService.getSubscribedAccessHistory(identity.id, pageSize, pageNumber)
    );
    activityLogsCollectionHook.setCollectionLoader(
      () => (pageSize: number, pageNumber: number) =>
        identityService.getIdentityActivityLogs(identity.id, pageNumber, pageSize)
    );
  }, [identity]);

  useEffect(() => {
    if (!identity) return;
    loadSubscriptions();
  }, [identity, subscriptionsPagination.cursor, subscriptionsPagination.pageSize]);

  const viewPreviousSearchResults = () => {
    navigate(-1);
  };

  const showUserDetailPopup = (popupType: USER_DETAIL_POPUP_TYPE) => {
    setUserDetailPopupType(popupType);
    setShowUserDetailPopup(true);
  };

  const exportUserDataJson = async () => {
    setAllUserDataLoading(true);
    if (identity && !allUserDataLoading) {
      const userData = await identityService.getAllUserData(identity.id);
      if (userData) {
        try {
          await saveAs(
            new Blob([JSON.stringify(userData, null, 1)], { type: 'application/json' }),
            `${identity.id}.json`
          );
          alert.setSuccessAlert(t('alertMessages.userDataExport.onSuccess'));
        } catch (e) {
          alert.setErrorAlert(t('alertMessages.userDataExport.onError'));
        }
      }
      setAllUserDataLoading(false);
    }
  };

  const invalidateAllSessionsOnClick = async () => {
    setInvalidateSessionsLoading(true);
    await invalidateAccessTokens(
      () => alert.setSuccessAlert(t('alertMessages.invalidateAllSessions.onSuccess')),
      () => alert.setErrorAlert(t('alertMessages.invalidateAllSessions.onError'))
    ).finally(() => setInvalidateSessionsLoading(false));
  };

  const deleteUserData = async () => {
    setUserDetailPopupType(USER_DETAIL_POPUP_TYPE.DELETE_USER_DATA);
    setShowUserDetailPopup(true);
  };

  const showAddSubscribedAccessDialog = useCallback(() => {
    setEntitlementData(undefined);
    setEntitlementDialogType(DIALOG_TYPE.ADD);
    setShowEntitlementDialog(true);
  }, []);

  const onEditEntitlement = useCallback((data: EntitlementRowData) => {
    setEntitlementData({
      ...data,
      id: {
        system: data.system,
        process: data.process,
        sourceId: data.sourceId,
      },
    });
    setEntitlementDialogType(DIALOG_TYPE.EDIT);
    setShowEntitlementDialog(true);
  }, []);

  const onUpdateEntitlement = useCallback(async () => {
    await identityHook.getIdentity();
    setShowEntitlementDialog(false);
  }, [identityHook]);

  const onRefreshCurrentEntitlements = useCallback(() => {
    if (!identity) return;
    entitlementsCollectionHook.setCollectionLoader(
      () => () => entitlementsService.getEntitlements(identity.id)
    );
  }, [entitlementsCollectionHook, identity]);

  if (identityError) {
    return (
      <AuthenticatedTemplate>
        <Stack direction="row" mb={2}>
          <IconButton onClick={viewPreviousSearchResults} sx={{ mr: 0.5 }}>
            <ChevronLeftOutlinedIcon />
          </IconButton>
          <Stack direction="column" flex={1}>
            <Typography variant="MHHeading" color="error">
              Identity not found
            </Typography>
            <Typography variant="MHCaption">
              The identity with ID &quot;{identityIdParam}&quot; could not be found. Please verify
              the ID and try again.
            </Typography>
          </Stack>
        </Stack>
      </AuthenticatedTemplate>
    );
  }

  return identity && !identityLoading ? (
    <AuthenticatedTemplate>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          mb: 1.5,
          alignItems: 'center',
        }}>
        <Stack direction="row">
          <IconButton onClick={viewPreviousSearchResults} sx={{ mr: 0.5 }}>
            <ChevronLeftOutlinedIcon />
          </IconButton>
          <Stack direction="column">
            <Typography variant="MHHeading">Identity information</Typography>
            <Typography variant="MHCaption">
              An overview of a user&apos;s personal information, subscribed access and activity log.
            </Typography>
          </Stack>
        </Stack>
        {!editModeEnabled && (
          <Button
            variant="brandContained"
            color="brandBlack"
            id="editUserDetails"
            onClick={() => setEditMode(true)}>
            Edit
          </Button>
        )}
        {editModeEnabled && (
          <Stack spacing={0.5} direction="row">
            <Button
              component="button"
              variant="brandContained"
              color="brandRed"
              id="cancelUserDetails"
              form="userDetailForm"
              disabled={isSavingEditedUserData}
              type="reset">
              Cancel
            </Button>
            <LoadingButton
              variant="brandContained"
              color="brandBlack"
              id="saveUserDetails"
              form="userDetailForm"
              loading={isSavingEditedUserData}
              type="submit">
              Save
            </LoadingButton>
          </Stack>
        )}
      </Stack>

      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          mb: 2,
        }}>
        <Button
          startIcon={<EditOutlinedIcon />}
          id="changePassword"
          onClick={() => showUserDetailPopup(USER_DETAIL_POPUP_TYPE.CHANGE_PASSWORD)}
          size="small"
          variant="brandOutlined"
          color="brandBlack"
          disabled={isSocialAccount}>
          Change password
        </Button>
        <Button
          startIcon={<EmailOutlinedIcon />}
          id="changeEmail"
          onClick={() => showUserDetailPopup(USER_DETAIL_POPUP_TYPE.CHANGE_EMAIL)}
          size="small"
          variant="brandOutlined"
          color="brandBlack"
          disabled={isSocialAccount}>
          Change email
        </Button>
        <LoadingButton
          startIcon={<LogoutOutlinedIcon />}
          size="small"
          variant="brandOutlined"
          color="brandBlack"
          disabled={identityLoading}
          loading={invalidateSessionsLoading}
          onClick={invalidateAllSessionsOnClick}>
          {t('invalidateAllSessionsButtonText')}
        </LoadingButton>
        <RoleAuthorizeShowGuard
          requiredRoles={[CUSSUP_ROLES.CUSSUP_DATA_PROTECTION]}
          currentRoles={roles}>
          <Button
            startIcon={<DeleteOutlinedIcon />}
            id="deleteUserData"
            size="small"
            variant="contained"
            color="brandRed"
            onClick={deleteUserData}>
            Delete user data
          </Button>
        </RoleAuthorizeShowGuard>
      </Stack>

      <Grid container spacing={1}>
        <Grid item xs={12} sm={10} md={8}>
          <UserDetailGrid
            identityHook={identityHook}
            editModeEnabled={editModeEnabled}
            setEditMode={setEditMode}
            setSavingEditedUserData={setIsSavingEditedUserData}></UserDetailGrid>
        </Grid>
      </Grid>
      {showEntitlementDialog && (
        <EntitlementDialog
          type={entitlementDialogType}
          identityId={identity.id}
          open={showEntitlementDialog}
          onClose={() => setShowEntitlementDialog(false)}
          onSave={onUpdateEntitlement}
          data={entitlementData}
        />
      )}
      {showEditUserDetailPopup && (
        <UserDetailPopup
          identityHook={identityHook}
          showPopup={showEditUserDetailPopup}
          popupClosed={setShowUserDetailPopup}
          popupType={userDetailPopupType}
        />
      )}

      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          my: 1.5,
        }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="MHHeading">Entitlements</Typography>
          <ToggleButtonGroup
            value={entitlementsView}
            exclusive
            size="small"
            onChange={(_, v) => v && setEntitlementsView(v)}>
            <ToggleButton value="enhanced">
              <Tooltip title="Enhanced view">
                <AutoAwesomeOutlinedIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="classic">
              <Tooltip title="Classic view">
                <ViewListOutlinedIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Button
          variant="brandContained"
          color="brandBlack"
          id="add-subscribed-access-button"
          onClick={showAddSubscribedAccessDialog}>
          Add
        </Button>
      </Stack>

      {entitlementsView === 'enhanced' ? (
        <EntitlementsEnhancedDataGrid
          collectionHook={entitlementsCollectionHook}
          onEdit={onEditEntitlement}
          identityId={identity.id}
          onRefreshCurrent={onRefreshCurrentEntitlements}
        />
      ) : (
        <>
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            The classic entitlements view is deprecated and will be removed in a future release.
            Please switch to the enhanced view.
          </Alert>
          <Box sx={{ mb: 1.5 }}>
            <TabPanel
              initialValue={SUBSCRIBED_ACCESS_TAB_VALUES.CURRENT}
              values={Object.values(SUBSCRIBED_ACCESS_TAB_VALUES)}
              setTabValue={setTabValue}
            />
          </Box>
          {tabValue === SUBSCRIBED_ACCESS_TAB_VALUES.CURRENT && (
            <EntitlementsTableContainer
              onEdit={onEditEntitlement}
              collectionHook={entitlementsCollectionHook}
            />
          )}
          {tabValue === SUBSCRIBED_ACCESS_TAB_VALUES.HISTORY && (
            <SubscribedAccessHistoryTableContainer
              collectionHook={subscribedAccessesHistoryCollectionHook}
            />
          )}
        </>
      )}

      <Typography
        variant="MHHeading"
        sx={{
          my: 3,
        }}>
        Subscriptions
      </Typography>
      <SubscriptionsTableContainer
        subscriptions={subscriptions}
        loading={subscriptionsLoading}
        error={subscriptionsError}
        page={subscriptionsPagination.page}
        pageSize={subscriptionsPagination.pageSize}
        totalCount={subscriptionsTotalCount}
        onPageChange={(newPage) =>
          subscriptionsPagination.handlePageChange(newPage, roleAssignments)
        }
        onPageSizeChange={subscriptionsPagination.handlePageSizeChange}
      />

      <Typography variant="MHHeading" my={3}>
        Activity
      </Typography>
      <UserActivityDataGrid pagedCollectionHook={activityLogsCollectionHook} identity={identity} />
      <Typography
        variant="MHHeading"
        sx={{
          my: 3,
        }}>
        Consents
      </Typography>
      <UserConsentDataGrid identityHook={identityHook} />

      <RoleAuthorizeShowGuard
        requiredRoles={[CUSSUP_ROLES.CUSSUP_DATA_PROTECTION]}
        currentRoles={roles}>
        <>
          <Typography
            variant="MHHeading"
            sx={{
              my: 3,
            }}>
            Export all user data
          </Typography>
          <LoadingButton
            variant="brandContained"
            color="brandBlack"
            id="getAllUserDataJsonBtn"
            onClick={exportUserDataJson}
            loading={allUserDataLoading}
            sx={{
              mr: 1,
            }}>
            Export user data (JSON)
          </LoadingButton>
          {allUserDataLoading && (
            <Typography
              variant="MHCaption"
              component="span"
              sx={{
                mx: 1,
              }}>
              {t('alertMessages.userDataExport.onLoading')}
            </Typography>
          )}
        </>
      </RoleAuthorizeShowGuard>
    </AuthenticatedTemplate>
  ) : (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <Spinner />
    </Box>
  );
};
